import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {checkUser, getDesign} from "../helpers/API";
import {DEV_MOD} from "../helpers/CONSTANT";
import {generateDxf} from "../ThreeJs/Files/Dxf/DxfGenerator";
import LoadingScreen from "../components/LoadingScreen";
import NotFound from "../components/errorsscreens/NotFound";

// Component responsible for verifying user, fetching design data, and generating a DXF file
const GenerateDxf = () => {
    // State to hold current design object
    const [currentDesign, setCurrentDesign] = useState(null);

    // Extract token and designID from the URL parameters
    const {token, designID} = useParams();

    // State flags for different loading and error states
    const [informationChecked, setInformationChecked] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);

    // Initial effect to verify user and load data unless in DEV_MOD
    useEffect(() => {
        if (DEV_MOD) {
            setInformationChecked("found"); // Bypass checks in development mode
            return;
        }
        if (token && designID) {
            verifyUser(); // Proceed with user verification
        } else {
            setInformationChecked("unauthenticated"); // Missing required parameters
        }
    }, [token, designID]);

    // Function to verify token and user
    const verifyUser = async () => {
        try {
            const res = await checkUser(token);
            if (res.status === 200) {
                setCurrentUser(res.data); // Save current user info
                await checkModelInfo();   // Proceed to fetch model/design
            } else {
                setInformationChecked("unauthenticated");
            }
        } catch (err) {
            console.error("User verification failed:", err);
            setInformationChecked("unauthenticated");
            setError("Failed to verify user.");
        }
    };

    // Function to fetch model/design details from API
    const checkModelInfo = async () => {
        try {
            const res = await getDesign(token, designID);
            if (res.status === 200) {
                setCurrentDesign(res.data.data); // Save model data
                setInformationChecked("found");
                setIsGenerating(true); // Trigger DXF generation process
            } else {
                setInformationChecked("not-found"); // Design not found
            }
        } catch (err) {
            console.error("Failed to retrieve design information:", err);
            setInformationChecked("not-found");
            setError("Failed to retrieve design information.");
        }
    };

    // Function that performs DXF generation from design data
    const handleGenerateDxf = async () => {
        if (!currentDesign) return;

        // console.log("Starting DXF generation...");
        let polygon;

        try {
            const parsed = JSON.parse(currentDesign.model_data); // { polygons: [...], lidar: {...} } OR maybe already an array

const dxfPolygons = Array.isArray(parsed) ? parsed : parsed?.polygons;

if (!Array.isArray(dxfPolygons)) {
//   console.error("Invalid model_data for DXF:", parsed);
  setError("Invalid model_data: polygons array missing.");
  return;
}

await generateDxf(dxfPolygons, currentDesign.name);


            // Notify opener window (if opened in a new tab/window) that DXF is ready
            if (window.opener) {
                window.opener.postMessage('dxfGenerated', '*');
            }
        } catch (err) {
            console.error("Failed to generate DXF:", err);
            setError("DXF generation failed."); // Set error if generation fails
        } finally {
            setIsGenerating(false); // Stop loading spinner regardless of success/failure
        }
    };

    // Trigger DXF generation once the required model data is loaded
    useEffect(() => {
        if (isGenerating) {
            handleGenerateDxf();
        }
    }, [isGenerating, currentDesign]);

    // Conditional rendering based on loading/authentication/design fetch state
    return (
        <>
            {informationChecked == null ? (
                <LoadingScreen informationChecked={informationChecked}/>
            ) : informationChecked === "unauthenticated" ? (
                <NotFound type={"token"}/>
            ) : informationChecked === "not-found" ? (
                <NotFound type={"design"}/>
            ) : (
                <LoadingScreen type={7}/> // Custom loading screen shown while generating DXF
            )
            }
        </>
    );
};

export default GenerateDxf;
